// WALLET-P1 in the in-memory API (docs/design/2026-10-27-wallet-pricing.md §6, §7): the client's
// Setup › Billing (`/v1/companies/{co}/wallet…`) and the operator's Operator › Billing
// (`/v1/operator/…`), with the API's rules —
// - client routes: owners, admins and agency operators (reading); members and client viewers 404;
//   an agency operator may not add funds (403: not their card; an operator credits by
//   adjustment), but may set the warning level (the API's `manager` access);
// - operator routes: agency users only (anyone else 404);
// - money is integer micros of USD; a top-up's bonus is the highest ladder rung at or under the
//   amount; the twin's "Stripe" pays at once (the checkout URL is the app's own return URL);
// - a tariff version prices tokens, images and per-unit lines (`unit_prices`; the known units at
//   launch prices when omitted) and marks up reported spend per category (`category_markups`);
// - no sample rows: every company starts with the wallet off and no entries, except Acme, the
//   demo company the rest of the fake seeds too (one paid top-up and a few agent runs).
import {
  KNOWN_TARIFF_UNITS,
  TariffCategoryMarkup,
  TariffPlanPrice,
  TariffUnitPrice,
} from "@companyos/schema/contracts";
import type { OperationOutput, RouteId } from "@companyos/sdk";
import { type Ctx, type Handler, json, notFound, page, problem, validation } from "./fake";
import { COMPANY_IDS, DE_IDS, uid } from "./fake-data";
import type { MeResponse } from "./types";

export const FAKE_WALLET_ROUTES = [
  "wallet.get",
  "wallet.entries",
  "wallet.entry",
  "wallet.spend",
  "wallet.topUp",
  "wallet.updateSettings",
  "operator.tariffs.list",
  "operator.tariffs.create",
  "operator.tariffs.retire",
  "operator.topupBonusTiers.list",
  "operator.topupBonusTiers.create",
  "operator.topupBonusTiers.update",
  "operator.topupBonusTiers.delete",
  "operator.wallet.get",
  "operator.wallet.putSettings",
  "operator.wallet.adjust",
  "operator.wallet.refund",
  "operator.wallet.ledgerTopUp",
  "operator.wallet.createTariff",
  "operator.wallet.retireTariff",
  "operator.walletMargin.get",
] as const satisfies readonly RouteId[];

type WalletView = OperationOutput<"wallet.get">;
type Line = OperationOutput<"wallet.entries">["data"][number];
type Entry = OperationOutput<"operator.wallet.adjust">;
type Tariff = OperationOutput<"operator.wallet.createTariff">;
type CatalogRow = OperationOutput<"operator.tariffs.list">["data"][number];
type Tier = OperationOutput<"operator.topupBonusTiers.list">["data"][number];
type OperatorView = OperationOutput<"operator.wallet.get">;
type Status = OperatorView["status"];
type Units = NonNullable<Entry["units"]>;

/** A ledger row plus the names the History shows (the API joins them). */
type Row = Entry & { agent_name: string | null; task_title: string | null };

type Checkout = OperatorView["checkouts"][number] & { receipt_url: string };

type CoWallet = {
  enabled: boolean;
  min: number;
  max: number;
  lowBp: number;
  pausedAt: string | null;
  rows: Row[];
  checkouts: Checkout[];
  tariffs: Tariff[];
};

type FakeCo = { id: string; slug: string; name: string };

const M = 1_000_000;
const LOW_FLOOR = 5 * M;

const actorNone = { actor_kind: null, actor_membership_id: null } as const;

export function createWalletFake(deps: { now: () => Date; companies: readonly FakeCo[] }) {
  const { now } = deps;
  let seq = 0;
  const id = () => uid(0x3a11, ++seq);
  const iso = () => now().toISOString();
  const today = () => iso().slice(0, 10);
  const monthOf = (at: string) => at.slice(0, 7);

  const catalog: CatalogRow[] = [
    {
      id: id(),
      version: 1,
      label: "2026-10 list",
      effective_from: "2026-10-01",
      input_per_m_micros: 500_000,
      output_per_m_micros: 2_000_000,
      image_micros: 150_000,
      unit_prices: KNOWN_TARIFF_UNITS.map((u) => ({ ...u })),
      reported_cost_markup_bp: 0,
      category_markups: [],
      model_prices: [],
      cost_markup_bp: null,
      plan_prices: [],
      currency: "USD",
      created_by_user_id: null,
      created_at: "2026-10-01T00:00:00.000Z",
      retired_at: null,
      retirable: false,
    },
  ];
  const tier = (min: number, bp: number, label: string | null): Tier => ({
    id: id(),
    min_amount_micros: min,
    bonus_bp: bp,
    label,
    effective_from: "2026-10-01",
    created_by_user_id: null,
    created_at: "2026-10-01T00:00:00.000Z",
    updated_at: "2026-10-01T00:00:00.000Z",
  });
  const tiers: Tier[] = [
    tier(200 * M, 500, null),
    tier(500 * M, 1000, "Growth"),
    tier(2000 * M, 1500, "Scale"),
  ];

  const wallets = new Map<string, CoWallet>();
  const walletOf = (companyId: string): CoWallet => {
    let w = wallets.get(companyId);
    if (!w) {
      w = {
        enabled: false,
        min: 20 * M,
        max: 10_000 * M,
        lowBp: 2000,
        pausedAt: null,
        rows: [],
        checkouts: [],
        tariffs: [],
      };
      wallets.set(companyId, w);
    }
    return w;
  };

  const balanceOf = (w: CoWallet) => w.rows.reduce((a, r) => a + r.amount_micros, 0);

  const effectiveTariff = (w: CoWallet): WalletView["tariff"] => {
    const day = today();
    const own = w.tariffs
      .filter((t) => !t.retired_at && t.effective_from <= day)
      .sort((a, b) => b.version - a.version)[0];
    if (own) return tariffView(own, "company");
    const plat = catalog
      .filter((t) => !t.retired_at && t.effective_from <= day)
      .sort((a, b) => b.version - a.version)[0];
    return plat ? tariffView(plat, "platform") : null;
  };

  function tariffView(
    t: Omit<CatalogRow, "retirable" | "created_by_user_id"> | Tariff,
    scope: "platform" | "company",
  ): NonNullable<WalletView["tariff"]> {
    return {
      scope,
      version: t.version,
      label: t.label,
      effective_from: t.effective_from,
      input_per_m_micros: t.input_per_m_micros,
      output_per_m_micros: t.output_per_m_micros,
      image_micros: t.image_micros,
      unit_prices: t.unit_prices.map((u) => ({ ...u })),
      reported_cost_markup_bp: t.reported_cost_markup_bp,
    };
  }

  const ladder = () =>
    [...tiers]
      .sort((a, b) => a.min_amount_micros - b.min_amount_micros)
      .map((t) => ({
        min_amount_micros: t.min_amount_micros,
        bonus_bp: t.bonus_bp,
        label: t.label,
      }));

  const rungFor = (amount: number) =>
    [...tiers]
      .filter((t) => t.min_amount_micros <= amount)
      .sort((a, b) => b.min_amount_micros - a.min_amount_micros)[0] ?? null;

  const lastTopup = (w: CoWallet) =>
    [...w.rows].reverse().find((r) => r.kind === "topup")?.amount_micros ?? null;

  const threshold = (w: CoWallet) => {
    const last = lastTopup(w);
    return Math.max(LOW_FLOOR, last === null ? 0 : Math.floor((last * w.lowBp) / 10_000));
  };

  const spentIn = (w: CoWallet, month: string) =>
    -w.rows
      .filter(
        (r) => (r.kind === "usage" || r.kind === "reversal") && monthOf(r.occurred_at) === month,
      )
      .reduce((a, r) => a + r.amount_micros, 0);

  function view(w: CoWallet, checkoutId: string | null = null): WalletView {
    const balance = balanceOf(w);
    const co = checkoutId ? w.checkouts.find((x) => x.id === checkoutId) : undefined;
    const last = lastTopup(w);
    return {
      enabled: w.enabled,
      currency: "USD",
      balance_micros: balance,
      held_micros: 0,
      available_micros: balance,
      spent_this_month_micros: spentIn(w, monthOf(iso())),
      last_topup_micros: last,
      low_watermark_bp: w.lowBp,
      low_threshold_micros: threshold(w),
      min_topup_micros: w.min,
      max_topup_micros: w.max,
      paused: w.pausedAt !== null,
      paused_at: w.pausedAt,
      tariff: effectiveTariff(w),
      ladder: ladder(),
      checkout_ready: true,
      // The twin's "Stripe" pays at once, so every known top-up is paid.
      checkout: co
        ? {
            id: co.id,
            status: "paid",
            amount_micros: co.amount_micros,
            bonus_micros: co.bonus_micros,
            completed_at: co.completed_at,
          }
        : null,
    };
  }

  const statusOf = (w: CoWallet): Status =>
    !w.enabled ? "off" : w.pausedAt ? "paused" : balanceOf(w) < threshold(w) ? "low" : "ok";

  function post(
    companyId: string,
    w: CoWallet,
    e: Partial<Row> & Pick<Row, "kind" | "amount_micros" | "label">,
  ): Row {
    const at = e.occurred_at ?? iso();
    const row: Row = {
      company_id: companyId,
      occurred_at: at,
      id: id(),
      meter_identity: `fake:${seq}`,
      balance_after_micros: balanceOf(w) + e.amount_micros,
      currency: "USD",
      cost_event_ref: null,
      tariff_id: null,
      tariff_version: null,
      units: null,
      run_id: null,
      task_id: null,
      membership_id: null,
      seat_id: null,
      workforce_agent_id: null,
      payment_ref: null,
      reverses_entry_id: null,
      cost_micros: null,
      agent_name: null,
      task_title: null,
      ...actorNone,
      created_at: at,
      ...e,
    };
    w.rows.push(row);
    if (w.enabled && row.balance_after_micros <= 0 && row.amount_micros < 0) w.pausedAt ??= at;
    if (row.balance_after_micros > 0 && row.amount_micros > 0) w.pausedAt = null;
    return row;
  }

  function topUp(companyId: string, w: CoWallet, amount: number, at?: string) {
    const rung = rungFor(amount);
    // The API's rule: amount × bp / 10,000 in micros, then down to a whole cent.
    const raw = rung ? Math.floor((amount * rung.bonus_bp) / 10_000) : 0;
    const bonus = raw - (raw % 10_000);
    const checkoutId = id();
    const receipt = `https://pay.stripe.test/receipts/${checkoutId}`;
    const ref = {
      provider: "stripe" as const,
      checkout_session: `cs_test_${seq}`,
      receipt_url: receipt,
    };
    const top = post(companyId, w, {
      kind: "topup",
      amount_micros: amount,
      label: "Top-up",
      payment_ref: ref,
      ...(at ? { occurred_at: at } : {}),
    });
    if (bonus > 0)
      post(companyId, w, {
        kind: "bonus",
        amount_micros: bonus,
        label: `Bonus ${rung?.bonus_bp ? rung.bonus_bp / 100 : 0}%`,
        units: { bonus_bp: rung?.bonus_bp ?? 0, tier_label: rung?.label ?? null },
        reverses_entry_id: null,
        ...(at ? { occurred_at: at } : {}),
      });
    w.checkouts.push({
      id: checkoutId,
      amount_micros: amount,
      bonus_micros: bonus,
      refunded_micros: 0,
      completed_at: top.occurred_at,
      receipt_url: receipt,
    });
    return { checkoutId, bonus, rung };
  }

  // Acme: the demo company's wallet (on, one paid top-up, a few runs of its DEs).
  {
    const acme = walletOf(COMPANY_IDS.acme);
    acme.enabled = true;
    const base = now().getTime();
    const day = (n: number) => new Date(base - n * 86_400_000).toISOString();
    topUp(COMPANY_IDS.acme, acme, 500 * M, day(6));
    const runs: [string, string, string, number, number, number][] = [
      [DE_IDS.riley, "Riley", "Source 20 candidates for the SDR role", 4, 182_000, 21_000],
      [DE_IDS.sam, "Sam", "Screen this week's applicants", 3, 96_000, 12_500],
      [DE_IDS.riley, "Riley", "Draft outreach messages", 1, 54_000, 8_000],
    ];
    // Agent work outside any task: two calls on one UTC day, shown as one daily line.
    for (const [tin, tout, amount] of [
      [8_000, 1_200, 6_400],
      [5_000, 900, 4_100],
    ] as const)
      post(COMPANY_IDS.acme, acme, {
        kind: "usage",
        amount_micros: -amount,
        label: "Agent work (no task)",
        occurred_at: day(2),
        units: { tokens_in: tin, tokens_out: tout, category: "tokens" },
        run_id: null,
        task_id: null,
        membership_id: null,
        tariff_id: catalog[0]?.id ?? null,
        tariff_version: 1,
        cost_micros: Math.round(amount * 0.3),
        agent_name: null,
        task_title: null,
      });
    for (const [i, [membership, name, task, ago, tin, tout]] of runs.entries()) {
      const charged = Math.round((tin * 500_000 + tout * 2_000_000) / M);
      const runId = uid(0x3a12, i + 1);
      const taskId = uid(0x3a13, i + 1);
      // Two calls per run, so a run's line groups them.
      for (const part of [0.6, 0.4]) {
        const amount = Math.round(charged * part);
        post(COMPANY_IDS.acme, acme, {
          kind: "usage",
          amount_micros: -amount,
          label: `${name} · ${task}`,
          occurred_at: day(ago),
          units: {
            tokens_in: Math.round(tin * part),
            tokens_out: Math.round(tout * part),
            category: "tokens",
          },
          run_id: runId,
          task_id: taskId,
          membership_id: membership,
          tariff_id: catalog[0]?.id ?? null,
          tariff_version: 1,
          cost_micros: Math.round(amount * 0.3),
          agent_name: name,
          task_title: task,
        });
      }
      // The sourcing run also searched the web: a unit-priced call (12 searches at $0.005).
      if (i === 0)
        post(COMPANY_IDS.acme, acme, {
          kind: "usage",
          amount_micros: -60_000,
          label: `${name} · ${task}`,
          occurred_at: day(ago),
          units: {
            category: "web_search",
            unit: "web_search",
            quantity: 12,
            unit_price_micros: 5_000,
          },
          run_id: runId,
          task_id: taskId,
          membership_id: membership,
          tariff_id: catalog[0]?.id ?? null,
          tariff_version: 1,
          cost_micros: 24_000,
          agent_name: name,
          task_title: task,
        });
    }
    // A correction on Sam's screening run: part of one call is given back.
    const samRun = uid(0x3a12, 2);
    post(COMPANY_IDS.acme, acme, {
      kind: "reversal",
      amount_micros: 2_000,
      label: "Correction · Screen this week's applicants",
      occurred_at: day(0),
      units: { category: "tokens" },
      run_id: samRun,
      task_id: uid(0x3a13, 2),
      membership_id: DE_IDS.sam,
      cost_micros: -600,
      agent_name: "Sam",
      task_title: "Screen this week's applicants",
    });
  }

  /** A usage row's category key and the label the client reads (the API sends it ready). */
  const categoryOf = (r: Row): [string, string] => {
    const c = r.units?.category ?? (r.units?.images ? "images" : "tokens");
    if (c === "tokens") return [c, "AI usage"];
    if (c === "images") return [c, "Images"];
    const unit = effectiveUnits().find((u) => u.unit === c);
    return [c, unit?.label ?? c.replace(/_/g, " ").replace(/^./, (s) => s.toUpperCase())];
  };
  /** The Spend row key a usage or reversal row belongs to in a group. */
  const spendKeyOf = (group: string, r: Row): string =>
    group === "agent"
      ? (r.membership_id ?? "none")
      : group === "task"
        ? (r.task_id ?? "none")
        : group === "category"
          ? categoryOf(r)[0]
          : r.occurred_at.slice(0, 10);
  const effectiveUnits = () =>
    catalog.filter((t) => !t.retired_at).sort((a, b) => b.version - a.version)[0]?.unit_prices ??
    [];

  // Access ---------------------------------------------------------------------------------------

  const client = (ctx: Ctx, write = false): Response | CoWallet => {
    const p = ctx.principal;
    if (!p?.manager) return notFound();
    if (write && p.operator)
      return problem(403, "forbidden", "Forbidden", "an agency operator credits by adjustment");
    return walletOf(p.company.id);
  };

  const grants = (me: MeResponse) => me.agency?.grants ?? [];
  const operator = (ctx: Ctx): Response | null => (ctx.me.agency ? null : notFound());
  const grantedCompany = (ctx: Ctx): Response | FakeCo => {
    if (!ctx.me.agency) return notFound();
    const cid = ctx.params.company_id;
    if (!grants(ctx.me).some((g) => g.company_id === cid)) return notFound();
    const co = deps.companies.find((c) => c.id === cid);
    return co ?? notFound();
  };

  const clientEntry = (r: Row) => {
    const { cost_micros: _c, agent_name: _a, task_title: _t, ...rest } = r;
    return rest;
  };
  const operatorEntry = (r: Row): Entry => {
    const { agent_name: _a, task_title: _t, ...rest } = r;
    return rest;
  };

  const sumUnits = (rows: Row[]): Units | null => {
    const u: Units = {};
    let any = false;
    for (const r of rows) {
      for (const k of ["tokens_in", "tokens_out", "images"] as const) {
        const v = r.units?.[k];
        if (v !== undefined) {
          u[k] = (u[k] ?? 0) + v;
          any = true;
        }
      }
    }
    return any ? u : null;
  };

  function lineOf(group: Row[]): Line {
    const newest = group[group.length - 1] as Row;
    const receipt = newest.payment_ref?.receipt_url ?? null;
    return {
      id: newest.id,
      kind: newest.kind,
      label: newest.label,
      occurred_at: newest.occurred_at,
      amount_micros: group.reduce((a, r) => a + r.amount_micros, 0),
      balance_after_micros: group.length > 1 ? null : newest.balance_after_micros,
      entries: group.length,
      run_id: newest.run_id,
      task_id: newest.task_id,
      task_title: newest.task_title,
      membership_id: newest.membership_id,
      agent_name: newest.agent_name,
      units: group.length > 1 || newest.kind === "usage" ? sumUnits(group) : newest.units,
      receipt_url: receipt,
    };
  }

  /** Newest first; a run's usage is one line, usage without a run one line per day. */
  function linesOf(rows: Row[], run: string | null, day: string | null = null): Line[] {
    if (day)
      return rows
        .filter((r) => r.kind === "usage" && !r.run_id && r.occurred_at.slice(0, 10) === day)
        .map((r) => lineOf([r]))
        .reverse();
    if (run)
      return rows
        .filter((r) => r.run_id === run)
        .map((r) => lineOf([r]))
        .reverse();
    const groups = new Map<string, Row[]>();
    const order: string[] = [];
    for (const r of rows) {
      const key =
        r.kind === "usage"
          ? r.run_id
            ? `run:${r.run_id}`
            : `day:${r.occurred_at.slice(0, 10)}`
          : `id:${r.id}`;
      if (!groups.has(key)) {
        groups.set(key, []);
        order.push(key);
      }
      groups.get(key)?.push(r);
    }
    return order
      .map((k) => lineOf(groups.get(k) ?? []))
      .sort((a, b) => b.occurred_at.localeCompare(a.occurred_at) || b.id.localeCompare(a.id));
  }

  const ordered = (w: CoWallet) =>
    [...w.rows].sort((a, b) => a.occurred_at.localeCompare(b.occurred_at));

  const idem = new Map<string, Entry>();
  const idemKey = (ctx: Ctx) => ctx.headers.get("Idempotency-Key");

  const isInt = (v: unknown, min = 0): v is number =>
    typeof v === "number" && Number.isInteger(v) && v >= min;
  const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

  type RatesBody = {
    label?: unknown;
    effective_from?: unknown;
    input_per_m_micros?: unknown;
    output_per_m_micros?: unknown;
    image_micros?: unknown;
    reported_cost_markup_bp?: unknown;
    unit_prices?: unknown;
    category_markups?: unknown;
    plan_prices?: unknown;
  };
  function ratesOf(body: unknown) {
    const b = (body ?? {}) as RatesBody;
    const label = typeof b.label === "string" ? b.label.trim() : "";
    if (!label || label.length > 60) return validation("label: 1 to 60 characters");
    if (typeof b.effective_from !== "string" || !ISO_DATE.test(b.effective_from))
      return validation("effective_from: a date");
    if (b.effective_from < today()) return validation("effective_from: today or later");
    for (const k of ["input_per_m_micros", "output_per_m_micros", "image_micros"] as const)
      if (!isInt(b[k])) return validation(`${k}: a whole number of micros`);
    const markup = b.reported_cost_markup_bp ?? 0;
    if (!isInt(markup) || markup > 100_000)
      return validation("reported_cost_markup_bp: 0 to 100000");
    // The per-unit lines and category markups: the contract's own rules (keys, each once, caps).
    const listOf = <T>(
      v: unknown,
      one: { safeParse(x: unknown): { success: boolean; data?: T } },
    ) => {
      if (!Array.isArray(v) || v.length > 50) return null;
      const out: T[] = [];
      for (const x of v) {
        const r = one.safeParse(x);
        if (!r.success) return null;
        out.push(r.data as T);
      }
      return out;
    };
    const units =
      b.unit_prices === undefined
        ? KNOWN_TARIFF_UNITS.map((u) => ({ ...u }))
        : listOf<TariffUnitPrice>(b.unit_prices, TariffUnitPrice);
    if (!units) return validation("unit_prices: unit, label, per, price_micros");
    if (new Set(units.map((u) => u.unit)).size !== units.length)
      return validation("unit_prices: each unit once");
    const cats =
      b.category_markups === undefined
        ? []
        : listOf<TariffCategoryMarkup>(b.category_markups, TariffCategoryMarkup);
    if (!cats) return validation("category_markups: category, markup_bp");
    if (new Set(cats.map((c) => c.category)).size !== cats.length)
      return validation("category_markups: each category once");
    // OPERATOR-CONSOLE: per-plan prices (a model and its two prices, or no model and a markup).
    const plans =
      b.plan_prices === undefined
        ? []
        : Array.isArray(b.plan_prices) && b.plan_prices.length <= 200
          ? b.plan_prices.map((x) => TariffPlanPrice.safeParse(x))
          : null;
    if (!plans || plans.some((r) => !r.success))
      return validation("plan_prices: a model and its two prices, or no model and a markup");
    const planPrices = plans.map((r) => r.data as TariffPlanPrice);
    if (new Set(planPrices.map((p) => `${p.plan_key}|${p.model ?? ""}`)).size !== planPrices.length)
      return validation("plan_prices: each plan and model once");
    return {
      label,
      effective_from: b.effective_from,
      input_per_m_micros: b.input_per_m_micros as number,
      output_per_m_micros: b.output_per_m_micros as number,
      image_micros: b.image_micros as number,
      unit_prices: units,
      reported_cost_markup_bp: markup,
      category_markups: cats,
      // HTVA-RUN-FIXES: per-model client prices and the cost markup (no UI yet).
      model_prices: [],
      cost_markup_bp: null,
      plan_prices: planPrices,
    };
  }

  const catalogRow = (t: CatalogRow): CatalogRow => ({
    ...t,
    retirable: !t.retired_at && t.effective_from > today(),
  });

  function operatorView(co: FakeCo): OperatorView {
    const w = walletOf(co.id);
    const month = monthOf(iso());
    const usage = w.rows.filter(
      (r) => (r.kind === "usage" || r.kind === "reversal") && monthOf(r.occurred_at) === month,
    );
    const charged = -usage.reduce((a, r) => a + r.amount_micros, 0);
    const cost = usage.reduce((a, r) => a + (r.cost_micros ?? 0), 0);
    return {
      company: { id: co.id, slug: co.slug, name: co.name },
      home_company: co.id === COMPANY_IDS.htva,
      status: statusOf(w),
      wallet: view(w),
      month: {
        key: month,
        charged_micros: charged,
        cost_micros: cost,
        margin_micros: charged - cost,
      },
      tariffs: [...w.tariffs].sort((a, b) => b.version - a.version),
      recent: ordered(w).reverse().slice(0, 20).map(operatorEntry),
      checkouts: w.checkouts.map(({ receipt_url: _r, ...c }) => c),
      // The fake's refunds settle at once: nothing is ever left to resume.
      pending_refunds: [],
    };
  }

  function marginLines(rows: Row[], key: (r: Row) => [string, string]) {
    const by = new Map<string, { key: string; label: string; charged: number; cost: number }>();
    for (const r of rows) {
      const [k, label] = key(r);
      const cur = by.get(k) ?? { key: k, label, charged: 0, cost: 0 };
      cur.charged += -r.amount_micros;
      cur.cost += r.cost_micros ?? 0;
      by.set(k, cur);
    }
    return [...by.values()]
      .sort((a, b) => b.charged - a.charged)
      .map((x) => ({
        key: x.key,
        label: x.label,
        charged_micros: x.charged,
        cost_micros: x.cost,
        margin_micros: x.charged - x.cost,
      }));
  }

  const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;

  const handlers: Record<(typeof FAKE_WALLET_ROUTES)[number], Handler> = {
    "wallet.get": (ctx) => {
      const w = client(ctx);
      return w instanceof Response ? w : json(view(w, ctx.url.searchParams.get("checkout")));
    },
    "wallet.entries": (ctx) => {
      const w = client(ctx);
      if (w instanceof Response) return w;
      const q = ctx.url.searchParams;
      const kind = q.get("kind");
      const from = q.get("from");
      const to = q.get("to");
      const rows = ordered(w).filter(
        (r) =>
          (!kind || r.kind === kind) &&
          (!from || r.occurred_at >= from) &&
          (!to || r.occurred_at < to),
      );
      const sg = q.get("spend_group");
      if (sg) {
        if (!["agent", "task", "day", "category"].includes(sg))
          return validation("spend_group: agent, task, day or category");
        // The fake's Spend rows key "No agent" / "No task" as "none"; the real API sends "".
        const sk = q.get("spend_key") === "none" ? "" : (q.get("spend_key") ?? "");
        const mine = rows
          .filter((r) => r.kind === "usage" || r.kind === "reversal")
          .filter((r) => {
            const k = spendKeyOf(sg, r);
            return (k === "none" ? "" : k) === sk;
          })
          .map((r) => lineOf([r]))
          .reverse();
        return json(page(mine, ctx.url));
      }
      return json(page(linesOf(rows, q.get("run"), q.get("day")), ctx.url));
    },
    "wallet.entry": (ctx) => {
      const w = client(ctx);
      if (w instanceof Response) return w;
      const r = w.rows.find((x) => x.id === ctx.params.id);
      return r ? json(clientEntry(r)) : notFound();
    },
    "wallet.spend": (ctx) => {
      const w = client(ctx);
      if (w instanceof Response) return w;
      const period = ctx.url.searchParams.get("period") ?? monthOf(iso());
      if (!MONTH.test(period)) return validation("period: YYYY-MM");
      const group = (ctx.url.searchParams.get("group") ?? "agent") as
        | "agent"
        | "task"
        | "day"
        | "category";
      if (!["agent", "task", "day", "category"].includes(group))
        return validation("group: agent, task, day or category");
      const usage = w.rows.filter(
        (r) => (r.kind === "usage" || r.kind === "reversal") && monthOf(r.occurred_at) === period,
      );
      const by = new Map<string, Row[]>();
      const labels = new Map<string, string>();
      for (const r of usage) {
        const [k, label] =
          group === "agent"
            ? [r.membership_id ?? "none", r.agent_name ?? "Other"]
            : group === "task"
              ? [r.task_id ?? "none", r.task_title ?? "No task"]
              : group === "category"
                ? categoryOf(r)
                : [r.occurred_at.slice(0, 10), r.occurred_at.slice(0, 10)];
        by.set(k, [...(by.get(k) ?? []), r]);
        labels.set(k, label);
      }
      const rows = [...by.entries()].map(([k, rs]) => ({
        key: k,
        label: labels.get(k) ?? k,
        charged_micros: -rs.reduce((a, r) => a + r.amount_micros, 0),
        runs: new Set(rs.map((r) => r.run_id).filter(Boolean)).size,
        tokens_in: rs.reduce((a, r) => a + (r.units?.tokens_in ?? 0), 0),
        tokens_out: rs.reduce((a, r) => a + (r.units?.tokens_out ?? 0), 0),
        images: rs.reduce((a, r) => a + (r.units?.images ?? 0), 0),
      }));
      rows.sort((a, b) =>
        group === "day" ? b.key.localeCompare(a.key) : b.charged_micros - a.charged_micros,
      );
      return json({
        period,
        group,
        total_micros: rows.reduce((a, r) => a + r.charged_micros, 0),
        rows,
      });
    },
    "wallet.topUp": (ctx) => {
      const w = client(ctx, true);
      if (w instanceof Response) return w;
      const p = ctx.principal;
      if (!p) return notFound();
      if (!w.enabled) return problem(409, "wallet_off", "Wallet off", "credit is not switched on");
      const amount = (ctx.body as { amount_micros?: unknown } | undefined)?.amount_micros;
      if (!isInt(amount, 1)) return validation("amount_micros: a positive whole number");
      if (amount < w.min || amount > w.max)
        return validation(`amount_micros: between ${w.min} and ${w.max}`);
      // The twin's Stripe pays at once: the credit is in before the browser comes back.
      const { checkoutId, bonus, rung } = topUp(p.company.id, w, amount);
      const origin = globalThis.location?.origin ?? "http://localhost";
      return json(
        {
          checkout_id: checkoutId,
          checkout_url: `${origin}/c/${p.company.slug}/setup/billing?topup=${checkoutId}`,
          amount_micros: amount,
          bonus_micros: bonus,
          bonus_bp: rung?.bonus_bp ?? 0,
          tier_label: rung?.label ?? null,
        },
        201,
      );
    },
    "wallet.updateSettings": (ctx) => {
      // Spec §7: the client sets its own warning level; an agency operator may not (403).
      const w = client(ctx, true);
      if (w instanceof Response) return w;
      const bp = (ctx.body as { low_watermark_bp?: unknown } | undefined)?.low_watermark_bp;
      if (!isInt(bp) || bp > 10_000) return validation("low_watermark_bp: 0 to 10000");
      w.lowBp = bp;
      return json(view(w));
    },

    "operator.tariffs.list": (ctx) =>
      operator(ctx) ??
      json({
        data: catalog
          .filter((t) => !t.retired_at)
          .sort((a, b) => b.version - a.version)
          .map(catalogRow),
      }),
    "operator.tariffs.create": (ctx) => {
      const refused = operator(ctx);
      if (refused) return refused;
      const r = ratesOf(ctx.body);
      if (r instanceof Response) return r;
      const row: CatalogRow = {
        id: id(),
        version: Math.max(0, ...catalog.map((t) => t.version)) + 1,
        ...r,
        currency: "USD",
        created_by_user_id: ctx.me.user.id,
        created_at: iso(),
        retired_at: null,
        retirable: false,
      };
      catalog.push(row);
      return json(catalogRow(row), 201);
    },
    "operator.tariffs.retire": (ctx) => {
      const refused = operator(ctx);
      if (refused) return refused;
      const t = catalog.find((x) => x.id === ctx.params.tariff_id);
      if (!t) return notFound();
      if (!catalogRow(t).retirable)
        return problem(409, "conflict", "Conflict", "a version in effect cannot be retired");
      t.retired_at = iso();
      return json(catalogRow(t));
    },
    "operator.topupBonusTiers.list": (ctx) =>
      operator(ctx) ??
      json({ data: [...tiers].sort((a, b) => a.min_amount_micros - b.min_amount_micros) }),
    "operator.topupBonusTiers.create": (ctx) => {
      const refused = operator(ctx);
      if (refused) return refused;
      const b = (ctx.body ?? {}) as {
        min_amount_micros?: unknown;
        bonus_bp?: unknown;
        label?: unknown;
      };
      if (!isInt(b.min_amount_micros, 1)) return validation("min_amount_micros: positive");
      if (!isInt(b.bonus_bp) || b.bonus_bp > 10_000) return validation("bonus_bp: 0 to 10000");
      if (tiers.some((t) => t.min_amount_micros === b.min_amount_micros))
        return problem(409, "conflict", "Conflict", "a tier starts at that amount already");
      const t: Tier = {
        ...tier(
          b.min_amount_micros,
          b.bonus_bp,
          typeof b.label === "string" && b.label.trim() ? b.label.trim() : null,
        ),
        effective_from: today(),
        created_by_user_id: ctx.me.user.id,
        created_at: iso(),
        updated_at: iso(),
      };
      tiers.push(t);
      return json(t, 201);
    },
    "operator.topupBonusTiers.update": (ctx) => {
      const refused = operator(ctx);
      if (refused) return refused;
      const t = tiers.find((x) => x.id === ctx.params.tier_id);
      if (!t) return notFound();
      const b = (ctx.body ?? {}) as {
        min_amount_micros?: unknown;
        bonus_bp?: unknown;
        label?: unknown;
      };
      if (b.min_amount_micros !== undefined) {
        if (!isInt(b.min_amount_micros, 1)) return validation("min_amount_micros: positive");
        if (tiers.some((x) => x !== t && x.min_amount_micros === b.min_amount_micros))
          return problem(409, "conflict", "Conflict", "a tier starts at that amount already");
        t.min_amount_micros = b.min_amount_micros;
      }
      if (b.bonus_bp !== undefined) {
        if (!isInt(b.bonus_bp) || b.bonus_bp > 10_000) return validation("bonus_bp: 0 to 10000");
        t.bonus_bp = b.bonus_bp;
      }
      if (b.label !== undefined)
        t.label = typeof b.label === "string" && b.label.trim() ? b.label.trim() : null;
      t.updated_at = iso();
      return json(t);
    },
    "operator.topupBonusTiers.delete": (ctx) => {
      const refused = operator(ctx);
      if (refused) return refused;
      const i = tiers.findIndex((x) => x.id === ctx.params.tier_id);
      if (i < 0) return notFound();
      const [gone] = tiers.splice(i, 1);
      return json(gone);
    },
    "operator.wallet.get": (ctx) => {
      const co = grantedCompany(ctx);
      return co instanceof Response ? co : json(operatorView(co));
    },
    "operator.wallet.putSettings": (ctx) => {
      const co = grantedCompany(ctx);
      if (co instanceof Response) return co;
      const b = (ctx.body ?? {}) as {
        enabled?: unknown;
        min_topup_micros?: unknown;
        max_topup_micros?: unknown;
      };
      if (typeof b.enabled !== "boolean") return validation("enabled: a boolean");
      if (!isInt(b.min_topup_micros, 1) || !isInt(b.max_topup_micros, 1))
        return validation("min_topup_micros, max_topup_micros: positive");
      if (b.min_topup_micros > b.max_topup_micros)
        return validation("min_topup_micros: not above max_topup_micros");
      if (b.enabled && co.id === COMPANY_IDS.htva)
        return problem(409, "conflict", "Conflict", "HTVA's own company keeps the wallet off");
      const w = walletOf(co.id);
      w.enabled = b.enabled;
      w.min = b.min_topup_micros;
      w.max = b.max_topup_micros;
      return json(operatorView(co));
    },
    "operator.wallet.adjust": (ctx) => {
      const co = grantedCompany(ctx);
      if (co instanceof Response) return co;
      const key = idemKey(ctx);
      if (!key) return validation("Idempotency-Key is required");
      const seen = idem.get(`adjust:${key}`);
      if (seen) return json(seen, 201);
      const b = (ctx.body ?? {}) as { amount_micros?: unknown; reason?: unknown };
      const amount = b.amount_micros;
      if (typeof amount !== "number" || !Number.isInteger(amount) || amount === 0)
        return validation("amount_micros: a non-zero whole number");
      if (Math.abs(amount) > 10_000 * M) return validation("at most $10,000 per adjustment");
      const reason = typeof b.reason === "string" ? b.reason.trim() : "";
      if (!reason) return validation("reason: required");
      const w = walletOf(co.id);
      if (!w.enabled) return problem(409, "wallet_off", "Wallet off", "credit is not switched on");
      if (amount < 0 && balanceOf(w) + amount < 0)
        return problem(409, "wallet_empty", "Not enough credit", "a debit cannot go below zero");
      const row = post(co.id, w, {
        kind: "adjustment",
        amount_micros: amount,
        label: amount > 0 ? `Credit: ${reason}` : `Adjustment: ${reason}`,
        actor_kind: "agency_user",
      });
      const out = operatorEntry(row);
      idem.set(`adjust:${key}`, out);
      return json(out, 201);
    },
    "operator.wallet.refund": (ctx) => {
      const co = grantedCompany(ctx);
      if (co instanceof Response) return co;
      const key = idemKey(ctx);
      if (!key) return validation("Idempotency-Key is required");
      const seen = idem.get(`refund:${key}`);
      if (seen) return json(seen, 201);
      const b = (ctx.body ?? {}) as {
        checkout_id?: unknown;
        amount_micros?: unknown;
        reason?: unknown;
      };
      const w = walletOf(co.id);
      const c = w.checkouts.find((x) => x.id === b.checkout_id);
      if (!c) return notFound();
      if (!isInt(b.amount_micros, 1)) return validation("amount_micros: positive");
      const reason = typeof b.reason === "string" ? b.reason.trim() : "";
      if (!reason) return validation("reason: required");
      if (b.amount_micros > c.amount_micros - c.refunded_micros)
        return problem(409, "conflict", "Conflict", "more than what is left of that payment");
      if (b.amount_micros > balanceOf(w))
        return problem(409, "wallet_empty", "Not enough credit", "never more than the balance");
      c.refunded_micros += b.amount_micros;
      const row = post(co.id, w, {
        kind: "refund",
        amount_micros: -b.amount_micros,
        label: "Refund to card",
        payment_ref: { provider: "stripe", refund: `re_test_${seq}` },
        actor_kind: "agency_user",
      });
      const out = operatorEntry(row);
      idem.set(`refund:${key}`, out);
      return json(out, 201);
    },
    // BILLING-LEDGER: a top-up paid outside a card checkout; credited with its bonus at once.
    "operator.wallet.ledgerTopUp": (ctx) => {
      const co = grantedCompany(ctx);
      if (co instanceof Response) return co;
      const b = (ctx.body ?? {}) as { checkout_id?: unknown; amount_micros?: unknown };
      if ((b.checkout_id === undefined) === (b.amount_micros === undefined))
        return validation("either checkout_id or amount_micros");
      // The twin has no waiting card checkout to settle.
      if (b.checkout_id !== undefined) return notFound();
      if (!isInt(b.amount_micros, 1)) return validation("amount_micros: positive");
      const w = walletOf(co.id);
      if (!w.enabled) return problem(409, "wallet_off", "Wallet off", "credit is not switched on");
      topUp(co.id, w, b.amount_micros);
      return json(operatorView(co), 201);
    },
    "operator.wallet.createTariff": (ctx) => {
      const co = grantedCompany(ctx);
      if (co instanceof Response) return co;
      const r = ratesOf(ctx.body);
      if (r instanceof Response) return r;
      const w = walletOf(co.id);
      const t: Tariff = {
        company_id: co.id,
        id: id(),
        scope: "company",
        source_tariff_id: null,
        version: Math.max(0, ...w.tariffs.map((x) => x.version)) + 1,
        ...r,
        currency: "USD",
        retired_at: null,
        actor_kind: "agency_user",
        actor_membership_id: null,
        created_at: iso(),
      };
      w.tariffs.push(t);
      return json(t, 201);
    },
    "operator.wallet.retireTariff": (ctx) => {
      const co = grantedCompany(ctx);
      if (co instanceof Response) return co;
      const t = walletOf(co.id).tariffs.find((x) => x.id === ctx.params.tariff_id);
      if (!t) return notFound();
      if (t.retired_at || t.effective_from <= today())
        return problem(409, "conflict", "Conflict", "a version in effect cannot be retired");
      t.retired_at = iso();
      return json(t);
    },
    "operator.walletMargin.get": (ctx) => {
      const refused = operator(ctx);
      if (refused) return refused;
      const month = ctx.url.searchParams.get("month") ?? monthOf(iso());
      if (!MONTH.test(month)) return validation("month: YYYY-MM");
      const companies = grants(ctx.me)
        .map((g) => deps.companies.find((c) => c.id === g.company_id))
        .filter((c): c is FakeCo => Boolean(c))
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((co) => {
          const w = walletOf(co.id);
          const usage = w.rows.filter(
            (r) =>
              (r.kind === "usage" || r.kind === "reversal") && monthOf(r.occurred_at) === month,
          );
          const charged = -usage.reduce((a, r) => a + r.amount_micros, 0);
          const cost = usage.reduce((a, r) => a + (r.cost_micros ?? 0), 0);
          return {
            company: { id: co.id, slug: co.slug, name: co.name },
            status: statusOf(w),
            balance_micros: balanceOf(w),
            spent_this_month_micros: spentIn(w, monthOf(iso())),
            charged_micros: charged,
            cost_micros: cost,
            margin_micros: charged - cost,
            agents: marginLines(usage, (r) => [r.membership_id ?? "none", r.agent_name ?? "Other"]),
            tasks: marginLines(usage, (r) => [r.task_id ?? "none", r.task_title ?? "No task"]),
          };
        });
      return json({ month, complete: true, incomplete_reasons: [], companies });
    },
  };

  /** `companies.get`'s `wallet_view`: a client (not an agency operator) of a wallet company. */
  const walletViewOf = (companyId: string, operator: boolean): boolean =>
    !operator && walletOf(companyId).enabled;

  return { handlers, walletViewOf };
}
